import * as React from 'react';

import {
  enumTrailerSortOption,
  enumTrailerStatus,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { cn, ComboBox } from '@rematter/ui-web';

import { useTrailersQuery } from '~/modules/transportation/hooks/queries/useTrailersQuery';
import useDebouncedValue from '~/shared/hooks/useDebounce';

export type TrailerSelectProps = {
  value?: string;
  onChange?: (value: string | number | null) => void;
  className?: string;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  excludeIds?: string[];
};

export const TrailerSelect = ({
  value,
  onChange,
  ...props
}: TrailerSelectProps) => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const { data, isLoading } = useTrailersQuery({
    searchTerm: debouncedSearchTerm || undefined,
    status: enumTrailerStatus.Available,
    sorting: [enumTrailerSortOption.LicensePlateAsc],
    limit: 50,
  });

  const comboBoxOptions = React.useMemo(
    () =>
      data?.trailers
        ?.filter((trailer) => !props.excludeIds?.includes(trailer.id))
        .map((trailer) => ({
          id: trailer.id,
          label: `${trailer.licensePlate} (${trailer.capacity} lbs)`,
        })) ?? [],
    [data?.trailers, props.excludeIds],
  );

  const handleSearch = React.useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  return (
    <ComboBox
      {...props}
      autoFocus={props.autoFocus}
      options={comboBoxOptions}
      value={value ? [value] : []}
      onChange={(values) => onChange?.(values?.[0] ?? null)}
      isLoading={isLoading}
      query={searchTerm}
      onChangeQuery={handleSearch}
      placeholder={
        props.placeholder ?? t.servicesView.form.placeholders.trailer
      }
      className={cn('@TrailerSelect', props.className)}
      getSelection={(ids) => {
        const trailer = data?.trailers?.find((t) => t.id === ids[0]);
        return trailer
          ? [{ label: `${trailer.licensePlate} (${trailer.capacity} lbs)` }]
          : [];
      }}
    />
  );
};
